import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import cn from 'classnames';
import {
  selectCardDavIntegration,
  selectIntegrations,
} from '@repo/redux/reducers/user/integrations';
import {
  promptToDisconnectIntegration,
  promptToDisconnectNotionIntegration,
  promptToResetNotionIntegration,
  unsupportedInMAS,
} from '@components/Integrations/utils';
import {
  IntegrationModals,
  renderDeviceIntegrationOption,
  useOnUnsupportedPlatform,
} from '@components/Integrations';
import { Section, SettingsSectionBox, SettingsSectionLabel } from '@views/Settings/components';
import {
  ExternallyManagedIntegration,
  integrationIsOnDevice,
  titleForDeviceIntegrationType,
  titleForIntegrationType,
} from '@repo/utils/models/Integration';
import {
  AddIntegration,
  ConnectedIntegration,
  IntegrationActionsMenu,
} from '@components/Integrations/Integration';
import {
  archiveIntegration,
  createCardDavIntegration,
  deleteExternalIntegration,
  deleteIntegration,
  fetchCardDavIntegration,
  fetchExternalIntegrations,
  syncExternalIntegration,
  reimportExternalIntegration,
} from '@repo/redux/actions/integrations';
import styles from './Integrations.module.scss';
import { navigateToSettings } from '@repo/redux/actions/ui';
import { ChevronRightIcon, OpenLinkIcon } from '@repo/icons';
import { useNavigate } from 'react-router-dom';
import { clearDeviceIntegrationSetupAborted } from '@repo/redux/actions/deviceIntegrations';
import { useAuthContext } from '@repo/hooks';
import {
  paywalledIntegrations,
  promptForPaywalledIntegration,
} from '@components/Integrations/UnsupportedIntegration';
import { SettingsSubPanel } from '@repo/utils/types';

export default function SettingsIntegrations({ renderAsRoute }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { getIsFreePlan } = useAuthContext();
  const userIntegrations = useSelector(selectIntegrations);
  const cardDavIntegration = useSelector(selectCardDavIntegration);
  const [activeModal, setActiveModal] = useState(null);
  const onUnsupported = useOnUnsupportedPlatform({
    setActiveModal,
    forceDarkMode: false,
    directUser: true,
  });
  const closeModal = () => setActiveModal(null);

  const sortedEnabledIntegrations = useMemo(() => {
    return userIntegrations.enabledIntegrations.sort((a, b) => {
      const aLabel = a.integration?.label || a.enablingProvider || '';
      const bLabel = b.integration?.label || b.enablingProvider || '';
      return aLabel.localeCompare(bLabel);
    });
  }, [userIntegrations.enabledIntegrations, userIntegrations.externallyManagedIntegrations]);

  const sortedConnectDeviceIntegrationOptions = useMemo(() => {
    const connectExternalOptions: ExternallyManagedIntegration[] = [
      cardDavIntegration ? undefined : ('card-dav' as ExternallyManagedIntegration),
      userIntegrations.hasExternallyManagedIntegration('chatgpt')
        ? undefined
        : ('chatgpt' as ExternallyManagedIntegration),
      userIntegrations.hasExternallyManagedIntegration('notion')
        ? undefined
        : ('notion' as ExternallyManagedIntegration),
      userIntegrations.hasExternallyManagedIntegration('zapier')
        ? undefined
        : ('zapier' as ExternallyManagedIntegration),
      userIntegrations.hasExternallyManagedIntegration('make')
        ? undefined
        : ('make' as ExternallyManagedIntegration),
    ].filter(Boolean);

    return userIntegrations.connectOnDeviceOptions
      .concat(['google', 'microsoft'])
      .concat(connectExternalOptions)
      .sort((a, b) => {
        const aLabel = titleForDeviceIntegrationType[a] || a || '';
        const bLabel = titleForDeviceIntegrationType[b] || b || '';
        return aLabel.localeCompare(bLabel);
      });
  }, [userIntegrations.connectOnDeviceOptions, userIntegrations.externallyManagedIntegrations]);

  useEffect(() => {
    if (!cardDavIntegration) {
      dispatch(fetchCardDavIntegration());
    }
    dispatch(fetchExternalIntegrations());
  }, []);

  useEffect(() => {
    if (userIntegrations.deviceIntegrationSetupAborted) {
      closeModal();
      dispatch(clearDeviceIntegrationSetupAborted());
    }
  }, [userIntegrations.deviceIntegrationSetupAborted]);

  return (
    <>
      <Section>
        <SettingsSectionLabel>Connected Integrations</SettingsSectionLabel>
        <SettingsSectionBox>
          {sortedEnabledIntegrations.map(({ integration, enablingProvider }) => {
            if (integration?.isOnDevice || enablingProvider) {
              return renderDeviceIntegrationOption({
                integrationProvider: integration?.provider || enablingProvider,
                className: styles.integrationOption,
                isInOnboarding: false,
                setActiveModal,
                onUnsupported,
              });
            }

            return (
              <ConnectedIntegration
                key={`${integration.type}_${integration.displayName}`}
                className={styles.integrationOption}
                integration={integration}
                isInOnboarding={false}
                removeIntegration={() => {
                  promptToDisconnectIntegration({
                    integration,
                    deleteIntegration: () => dispatch(deleteIntegration(integration)),
                    archiveIntegration: () => dispatch(archiveIntegration(integration)),
                  });
                }}
              />
            );
          })}
          {cardDavIntegration && (
            <AddIntegration
              key="card-dav"
              className={styles.integrationOption}
              provider="card-dav"
              label={titleForIntegrationType['card-dav']}
              onClick={() => {
                if (renderAsRoute) {
                  navigate('/settings/integrations/card-dav');
                } else {
                  dispatch(navigateToSettings({ panel: 'integrations', subPanel: 'card-dav' }));
                }
              }}
              isInOnboarding={false}
              altIcon={
                <ChevronRightIcon
                  width="14"
                  height="14"
                  viewBox="-5 -0.5 15 15"
                  className={cn('integration-option-action-icon', 'chevron-right-icon')}
                />
              }
            />
          )}
          {userIntegrations.externallyManagedIntegrations
            .valueSeq()
            .toArray()
            .map((integration) => {
              // onClick will vary depending on the integration
              let onClick = () => {
                if (renderAsRoute) {
                  navigate(`/settings/integrations/${integration.provider}`);
                } else {
                  dispatch(
                    navigateToSettings({
                      panel: 'integrations',
                      subPanel: integration.provider as SettingsSubPanel,
                    }),
                  );
                }
              };
              let altIcon = (
                <ChevronRightIcon
                  width="14"
                  height="14"
                  viewBox="-5 -0.5 15 15"
                  className={cn('integration-option-action-icon', 'chevron-right-icon')}
                />
              );

              if (['chatgpt'].includes(integration.provider)) {
                onClick = undefined;

                altIcon = (
                  <IntegrationActionsMenu
                    integration={integration}
                    noSyncing
                    removeIntegration={() => {
                      dispatch(deleteExternalIntegration(integration));
                    }}
                    toggleMenu={() => {}}
                    additionalOptions={[
                      {
                        title: 'Go to ChatGPT',
                        action: () => {
                          window.open('https://chatgpt.com/g/g-xYq31Ba0m-clay', '_blank');
                        },
                        icon: OpenLinkIcon,
                      },
                    ]}
                  />
                );
              }

              if (['notion'].includes(integration.provider)) {
                onClick = undefined;
                altIcon = (
                  <IntegrationActionsMenu
                    integration={integration}
                    syncing={integration.isFetching}
                    removeIntegration={() =>
                      promptToDisconnectNotionIntegration({
                        deleteIntegration: () => dispatch(deleteExternalIntegration(integration)),
                      })
                    }
                    syncIntegration={() => dispatch(syncExternalIntegration(integration))}
                    resetIntegration={() =>
                      promptToResetNotionIntegration(() =>
                        dispatch(reimportExternalIntegration(integration)),
                      )
                    }
                    toggleMenu={() => {}}
                    additionalOptions={[
                      {
                        title: 'Go to Notion',
                        action: () => {
                          window.open('https://notion.so', '_blank');
                        },
                        icon: OpenLinkIcon,
                      },
                    ]}
                  />
                );
              }

              return (
                <AddIntegration
                  key={integration.provider}
                  className={cn(styles.integrationOption, !onClick && styles.notClickable)}
                  provider={integration.provider}
                  label={titleForIntegrationType[integration.provider]}
                  onClick={onClick}
                  isInOnboarding={false}
                  altIcon={altIcon}
                />
              );
            })}
        </SettingsSectionBox>
      </Section>
      <Section>
        <SettingsSectionLabel>All Integrations</SettingsSectionLabel>
        <SettingsSectionBox>
          {sortedConnectDeviceIntegrationOptions.map((provider) => {
            if (window.desktopIntegrations?.appInfo?.isMAS && unsupportedInMAS(provider)) {
              return null;
            }

            if (integrationIsOnDevice(provider)) {
              return renderDeviceIntegrationOption({
                integrationProvider: provider,
                className: styles.integrationOption,
                isInOnboarding: false,
                setActiveModal,
                onUnsupported,
              });
            }

            if (provider === 'card-dav') {
              return (
                <AddIntegration
                  key="card-dav"
                  className={styles.integrationOption}
                  provider="card-dav"
                  label={titleForIntegrationType['card-dav']}
                  onClick={() => {
                    dispatch(createCardDavIntegration());

                    if (renderAsRoute) {
                      navigate(`/settings/integrations/card-dav`);
                    } else {
                      dispatch(
                        navigateToSettings({
                          panel: 'integrations',
                          subPanel: 'card-dav' as SettingsSubPanel,
                        }),
                      );
                    }
                  }}
                  isInOnboarding={false}
                />
              );
            }

            let onClick = () => setActiveModal(provider);

            if (provider === 'chatgpt') {
              onClick = () => {
                window.open('https://chatgpt.com/g/g-xYq31Ba0m-clay', '_blank');
              };
            }

            if (provider === 'notion') {
              onClick = () => {
                window.open('https://go.clay.earth/connect/notion', '_blank');
              };
            }

            if (provider === 'zapier') {
              onClick = () => {
                if (renderAsRoute) {
                  navigate(`/settings/integrations/zapier`);
                } else {
                  dispatch(
                    navigateToSettings({
                      panel: 'integrations',
                      subPanel: 'zapier' as SettingsSubPanel,
                    }),
                  );
                }
              };
            }

            if (provider === 'make') {
              onClick = () => {
                if (renderAsRoute) {
                  navigate(`/settings/integrations/make`);
                } else {
                  dispatch(
                    navigateToSettings({
                      panel: 'integrations',
                      subPanel: 'make' as SettingsSubPanel,
                    }),
                  );
                }
              };
            }

            if (paywalledIntegrations.includes(provider) && getIsFreePlan()) {
              onClick = () => {
                promptForPaywalledIntegration({ integrationType: provider, forceDarkMode: false });
              };
            }

            return (
              <AddIntegration
                key={provider}
                className={styles.integrationOption}
                provider={provider}
                label={titleForIntegrationType[provider] || provider}
                onClick={onClick}
                isInOnboarding={false}
              />
            );
          })}
        </SettingsSectionBox>
      </Section>
      <IntegrationModals
        activeModal={activeModal}
        closeModal={closeModal}
        forceDarkMode={false}
        trackConnection={() => {}}
        renderLocation="settings"
      />
    </>
  );
}
